import {ReactNode} from "react";
import PKCE from "js-pkce";
import StorageItem from "../shared/StorageItem";
import portalConfiguration from "../shared/configuration/PortalConfiguration";
import Portal from "../portal/Portal";
import {AuthProvider, Information} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import OAuthContext from "./OAuthContext";
import OAuthError from "../shared/exception/OAuthError";
import FunctionHelper from "../shared/helper/FunctionHelper";
import PortalPureComponent from "../shared/components/PortalPureComponent";

export default class OAuth extends PortalPureComponent<any, any> {
	public static route: string = "oauth";
	private oauthContext: OAuthContext;
	private redirectTarget: string;

	constructor(props: any) {
		super(props);

		this.oauthContext = new OAuthContext();
		this.redirectTarget = portalConfiguration.baseUrl.toString() + Portal.route;
	}

	private redirectToPortal(): void {
		// clear PKCE tokens
		sessionStorage.removeItem(StorageItem.OAUTH_VERIFIER);
		sessionStorage.removeItem(StorageItem.OAUTH_STATE);

		window.location.href = this.redirectTarget;
	}

	private getProvider(): AuthProvider {
		let portalInfo: Information = this.oauthContext.info;

		let providerName: string | null = localStorage.getItem(StorageItem.PROVIDER);
		for (let oauthProvider of FunctionHelper.applyValue(portalInfo.oauth, Information.getOauthDefault())) {
			if (oauthProvider.name === providerName) {
				return oauthProvider;
			}
		}

		this.redirectToPortal();
		return {} as AuthProvider;
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let redirectTarget: string | null = sessionStorage.getItem(StorageItem.OAUTH_REDIRECT);
		if (redirectTarget !== null) {
			this.redirectTarget = redirectTarget;
			sessionStorage.removeItem(StorageItem.OAUTH_REDIRECT);
		}

		try {
			await this.oauthContext.initialize();
		} catch (e) {
			this.redirectToPortal();
			return;
		}

		let portalUrl = portalConfiguration.portalUrl.toString();
		let provider: AuthProvider = this.getProvider();
		let pkce: PKCE = new PKCE({
			client_id: FunctionHelper.applyValue(provider.clientId, ""),
			redirect_uri: portalUrl + OAuth.route,
			authorization_endpoint: provider.oidc!.authorizationEndpoint,
			token_endpoint: provider.oidc!.tokenEndpoint,
			requested_scopes: ""
		});

		let url: URL = new URL(window.location.href);
		let searchParams: URLSearchParams = url.searchParams;

		if (searchParams.get("error")) {
			sessionStorage.setItem(StorageItem.OAUTH_ERROR, JSON.stringify({
				name: searchParams.get("error"),
				description: searchParams.get("error_description")
			} as OAuthError));

			this.redirectToPortal();
			return;
		}

		let additionalParams: any = {};
		let response: any = await pkce.exchangeForAccessToken(url.toString(), additionalParams);

		if (FunctionHelper.exists(response.error)) {
			sessionStorage.setItem(StorageItem.OAUTH_ERROR, JSON.stringify({
				name: response.error,
				description: response.error_description,
				raw: JSON.stringify(response)
			} as OAuthError));
		} else {
			sessionStorage.setItem(StorageItem.ACCESS_TOKEN, response.access_token!);

			if (FunctionHelper.exists(response.refresh_token)) {
				localStorage.setItem(StorageItem.REFRESH_TOKEN, response.refresh_token);
			}

			if (FunctionHelper.exists(response.id_token)) {
				sessionStorage.setItem(StorageItem.ID_TOKEN, response.id_token!);
			}
		}

		this.redirectToPortal();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return null;
	}
}